import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import clsx from 'clsx'
import { currencyNames } from 'lib/settings/currencies'
import { useCallback, type JSX } from 'react'
import { create } from 'zustand'
import Icon from '../Icon/Icon'

interface CurrencySelectorButtonRenderProps {
  onClick: () => void
}

export interface CurrencySelectorButtonProps {
  /**
   * A function that renders the intended button for the help center trigger
   */
  render: (buttonProps: CurrencySelectorButtonRenderProps) => JSX.Element
}

export interface CurrencySelectorStore {
  dialogOpen: boolean
  selectedValue: Currency
  openDialog: () => void
  hideDialog: (selectedValue: Currency) => void
}

export const currencySymbols = {
  USD: '$',
  CAD: '$',
  EUR: '€',
  GBP: '£',
  AUD: '$',
  HKD: '$',
}

export const useCurrencySelector = create<CurrencySelectorStore>((set) => ({
  dialogOpen: false,
  selectedValue: 'USD',
  openDialog: () => set({ dialogOpen: true }),
  hideDialog: (selectedValue) => set({ selectedValue, dialogOpen: false }),
}))

export const CurrencySelectorButton = ({
  render,
}: CurrencySelectorButtonProps) => {
  const openDialog = useCurrencySelector((state) => state.openDialog)

  const onClick = useCallback(() => {
    openDialog()
  }, [openDialog])

  return render({ onClick })
}

export default function CurrencySelector() {
  const [selectedValue, isOpen, hide] = useCurrencySelector((state) => [
    state.selectedValue,
    state.dialogOpen,
    state.hideDialog,
  ])

  return (
    <Transition
      show={isOpen}
      enter="ease-out duration-100"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200 delay-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Dialog
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={() => hide(selectedValue)}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div
            className="fixed inset-0 bg-theme-primary/50"
            aria-hidden="true"
          />
          <div className="relative flex min-h-full w-full items-center justify-center p-4">
            <TransitionChild
              enter="ease-out duration-300 delay-100"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogPanel className="relative w-full md:max-w-[475px] bg-white p-12 rounded-lg drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)]">
                <DialogTitle as="h2" className="type-body-3 text-center">
                  Choose a currency
                </DialogTitle>
                <div className="flex flex-col mt-6 border border-neutral-400 rounded-lg overflow-hidden">
                  {currencyNames.map(({ name, value }, currencyKey) => (
                    <div
                      className={clsx(
                        '[&:not(:last-child)]:border-b border-neutral-300 px-4 py-[1.1875rem] type-body-8 hover:bg-neutral-200 cursor-pointer',
                        { 'bg-neutral-200': value === selectedValue },
                      )}
                      onClick={() => hide(value)}
                      key={`${currencyKey}-${value}`}
                    >
                      {name}
                    </div>
                  ))}
                </div>
                <button
                  className="w-6 h-6 flex items-center justify-center absolute right-6 top-6"
                  onClick={() => hide(selectedValue)}
                >
                  <Icon icon="close" className="w-full h-full" />
                </button>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
